export const ALGOLIA_SEARCH_ATTRIBUTION_KEY = 'algoliaSearchAttribution';
export const ALGOLIA_PURCHASE_ATTRIBUTION_KEY = 'algolia_purchase_attributions';

function normalizeAttributionValue(value) {
  if (value == null || value === '') {
    return null;
  }

  return value;
}

export function buildAlgoliaSearchAttribution(payload = {}) {
  const objectID = normalizeAttributionValue(payload.objectID);
  const index = normalizeAttributionValue(payload.index || payload.__indexName || payload.indexName);
  const queryID = normalizeAttributionValue(payload.queryID || payload.__queryID);
  const position = normalizeAttributionValue(payload.position || payload.__position);

  if (!objectID || !index) {
    return null;
  }

  return {
    objectID,
    index,
    queryID,
    position: position == null ? null : Number(position),
  };
}

export function setAlgoliaSearchAttribution(payload = {}) {
  const attribution = buildAlgoliaSearchAttribution(payload);

  if (!attribution || typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  window.localStorage.setItem(ALGOLIA_SEARCH_ATTRIBUTION_KEY, JSON.stringify(attribution));

  publishAttributionToPixel(attribution);

  return attribution;
}

function publishAttributionToPixel(attribution) {
  if (!attribution || typeof window === 'undefined') {
    return;
  }

  try {
    window.Shopify?.analytics?.publish('algolia_search_attribution', {
      objectID: String(attribution.objectID),
      index: attribution.index,
      queryID: attribution.queryID,
      position: attribution.position,
    });
  } catch (e) {
    // Silently fail if Shopify analytics is not available
  }
}

export function getAlgoliaSearchAttribution() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  const rawValue = window.localStorage.getItem(ALGOLIA_SEARCH_ATTRIBUTION_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    window.localStorage.removeItem(ALGOLIA_SEARCH_ATTRIBUTION_KEY);
    return null;
  }
}

export function clearAlgoliaSearchAttribution() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  window.localStorage.removeItem(ALGOLIA_SEARCH_ATTRIBUTION_KEY);
}

export function savePurchaseAttribution(trackingPayload) {
  if (!trackingPayload || !trackingPayload.queryID || typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const raw = window.localStorage.getItem(ALGOLIA_PURCHASE_ATTRIBUTION_KEY);
    const stored = raw ? JSON.parse(raw) : [];

    const variantId = trackingPayload.variantId || trackingPayload.objectID;
    const exists = stored.some((item) => item.variantId === String(variantId));

    if (!exists) {
      stored.push({
        variantId: String(variantId),
        productId: trackingPayload.productId ? String(trackingPayload.productId) : null,
        objectID: String(trackingPayload.objectID),
        queryID: trackingPayload.queryID,
        index: trackingPayload.index,
        position: trackingPayload.position,
        timestamp: Date.now(),
      });

      window.localStorage.setItem(ALGOLIA_PURCHASE_ATTRIBUTION_KEY, JSON.stringify(stored));
    }
  } catch (e) {
    // Fail silently
  }
}

function normalizeProductId(product) {
  if (!product) {
    return null;
  }

  const candidates = [
    product.id,
    product.productId,
    product.product_id,
    product?.product?.id,
    product?.cprops?.productDetails?.id,
  ];

  const match = candidates.find((value) => value != null && value !== '');

  return match == null ? null : String(match);
}

export function getMatchingAlgoliaSearchAttribution(product) {
  const attribution = getAlgoliaSearchAttribution();

  if (!attribution || !product) {
    return null;
  }

  const objectID = String(attribution.objectID);

  const productId = normalizeProductId(product);
  if (productId && objectID === productId) {
    return attribution;
  }

  const variants = product.variants || (product.product && product.product.variants) || [];
  if (variants.some((v) => v && String(v.id) === objectID)) {
    return attribution;
  }

  return null;
}

export function buildAlgoliaAddToCartTrackingPayload({
  product,
  variantId = null,
  quantity = null,
  attribution = null,
} = {}) {
  const matchedAttribution = attribution || getMatchingAlgoliaSearchAttribution(product);

  if (!matchedAttribution) {
    return null;
  }

  return {
    ...matchedAttribution,
    productId: normalizeProductId(product),
    variantId: variantId == null ? null : String(variantId),
    quantity: quantity == null ? null : Number(quantity),
  };
}

export function initAlgoliaSearchAttributionGlobals() {
  if (typeof window === 'undefined') {
    return;
  }

  window.setAlgoliaSearchAttribution = setAlgoliaSearchAttribution;
}
